/**
 * Canvas Panel Preview (dev only)
 *
 * Mounts `CanvasPanel` inside a `CanvasProvider` with multiple seeded
 * canvases and surfaces, so visual changes to the surface header (action
 * menu, copy/move/dismiss) and the canvas tabs can be validated without
 * running a real agent. Visit `/dev/canvas-panel-preview` in dev mode.
 */

import { useMemo } from 'react';
import { PlatformEventContext } from '@/contexts/PlatformEventContext';
import { CanvasProvider, useCanvases } from '@/contexts/CanvasContext';
import { CanvasPanel } from '@/components/canvas/CanvasPanel';
import type { Canvas, Surface } from '@/types/canvases';
import { cleanDisplayText } from '@/utils/display-text';
import overflowPlanMarkdown from './canvas-overflow-plan-fixture.md?raw';

/**
 * Dev-only chip strip that mounts inside `CanvasProvider` and dispatches
 * `requestScrollToSurface` for each known surface. Lets us visually verify
 * the scroll + blue-glow highlight pathway here without needing an
 * authenticated operator session.
 */
function DevHighlightChips() {
  const { surfaces, requestScrollToSurface } = useCanvases();
  return (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-border bg-muted/40 px-4 py-2">
      <span className="mr-2 text-xs text-muted-foreground">Click to scroll + highlight:</span>
      {[...surfaces.values()].map((s) => (
        <button
          key={`${s.canvasId}:${s.surfaceId}`}
          type="button"
          data-testid={`dev-chip-${s.surfaceId}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => requestScrollToSurface(s.canvasId, s.surfaceId)}
        >
          {cleanDisplayText(s.title) || s.surfaceId}
        </button>
      ))}
    </div>
  );
}

const noopEventContext = {
  subscribe: () => () => {},
  isConnected: false,
  diagnostics: {
    connectedSince: null,
    disconnectCount: 0,
    lastDisconnectReason: null,
    lastDisconnectTime: null,
    reconnectAttempts: 0,
  },
};

const CONV_ID = 'conv-preview';

// Mix of short and long canvas titles to exercise the tab strip's
// horizontal scroll + overflow menu. Long titles (resembling real agent
// session names) force overflow at typical viewport widths so visual
// validation of the scroll-into-view + overflow-dropdown behavior is
// reliable without resizing.
const fakeCanvases: Canvas[] = [
  {
    canvasId: 'cvs-research',
    conversationId: CONV_ID,
    title: 'Research',
    isDefault: true,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-drafts',
    conversationId: CONV_ID,
    title: '**Drafts &amp; Notes**',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-archive',
    conversationId: CONV_ID,
    title: 'Archive',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-paper-scout-latest',
    conversationId: CONV_ID,
    title: 'AI Paper Scout - Latest Run Analysis',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-paper-scout-iter',
    conversationId: CONV_ID,
    title: 'AI Paper Scout - Increase Max Iterations',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-summary',
    conversationId: CONV_ID,
    title: 'Run Summary Agent - Latest Run Analysis',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-third',
    conversationId: CONV_ID,
    title: 'Canvas 3',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-skill-editor',
    conversationId: CONV_ID,
    title: 'Skill Editor (fill)',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-two-editors',
    conversationId: CONV_ID,
    title: 'Two Editors (grow)',
    isDefault: false,
    layout: 'stack',
    createdAt: new Date().toISOString(),
  },
];

const skillEditorMarkdown = `## GitHub Repo Processing

Process a GitHub repository URL and store its content in the knowledge base.

### Input
- \`url\`: The GitHub repo URL
- \`owner\`, \`repo\`: Extracted from the URL

### Step 1: Retrieve metadata via GitHub API

Use the API:

\`\`\`bash
curl -H "Accept: application/vnd.github+json" https://api.github.com/repos/{owner}/{repo}
\`\`\`

### Step 2: Build the document

\`\`\`markdown
# GitHub: {owner}/{repo}

Description, language, stars, forks, license, topics.
\`\`\`

### Step 3: Store

Upload via presigned URL with content-type \`text/markdown\`. Mark the document as completed once the upload returns 200.

### Error handling
- Private/non-existent repo (API 404) → status \`failed\`
- README missing → still store metadata, mark \`partial\`
- Rate limited → fall back to web browse
`;

const fakeSurfaces: Surface[] = [
  {
    canvasId: 'cvs-research',
    surfaceId: 'srf-summary',
    runId: 'run-1',
    // Deliberately dirty: agents emit markdown + HTML entities in titles;
    // the UI must render 'YC Performance & Outcomes — Quarterly summary'.
    title: '**YC Performance &amp; Outcomes — Quarterly summary**',
    version: 3,
    spec: {
      updates: [
        {
          id: 'root',
          component: {
            type: 'Text',
            props: { text: 'A short summary of the quarter goes here.', variant: 'p' },
          },
        },
      ],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    canvasId: 'cvs-research',
    surfaceId: 'srf-metrics',
    runId: 'run-1',
    title: 'Key metrics',
    version: 1,
    spec: {
      updates: [
        {
          id: 'root',
          component: {
            type: 'Text',
            props: { text: 'Conversion rate: 4.2% — up 0.8 pp vs last quarter.', variant: 'p' },
          },
        },
      ],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Agent-drift repair pipeline: raw made-up MarkdownEditor props as an agent
  // might persist them. `readOnly` is aliased to `editable` by AuiSurface's
  // normalize pass (top editor renders read-only content); wholly unknown
  // props fall through to the visible empty-content notice (bottom editor).
  {
    canvasId: 'cvs-research',
    surfaceId: 'srf-made-up-props',
    runId: 'run-1',
    title: 'Made-up props (repair + fallback)',
    version: 1,
    spec: {
      updates: [
        {
          id: 'root',
          component: { type: 'Column', props: { gap: 4 }, children: ['repaired', 'broken'] },
        },
        {
          id: 'repaired',
          component: {
            type: 'MarkdownEditor',
            props: {
              title: 'repaired.md — `readOnly: true` aliased to `editable: false`',
              content:
                '# Repaired\n\nThis editor was sent with `readOnly: true` instead of `editable` — the alias normalizer fixed it at render time.\n\n<div class="note">Standalone block-level HTML — the html fallback visitor must wrap this in a paragraph (root can only hold element/decorator nodes; Lexical #282).</div>\n\n<!-- top-level HTML comment, same root-level html node path -->\n\nText after the HTML blocks.',
              readOnly: true,
              maxHeight: '160px',
            },
          },
        },
        {
          id: 'broken',
          component: {
            type: 'MarkdownEditor',
            props: {
              title: 'broken.md — no recognizable content prop',
              src: 'vfs:///delegated/run-01kz998y65fz1ypm6ve4.md?v=1785944457599',
              locked: true,
            },
          },
        },
      ],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Single-surface canvas whose root Column contains a meta Card and a
  // MarkdownEditor with no maxHeight. Exercises the fill cascade:
  // Surface.Card → CardContent → root Column → AuiMarkdownEditor wrapper.
  {
    canvasId: 'cvs-skill-editor',
    surfaceId: 'srf-skill-editor',
    runId: 'run-skill',
    title: 'GitHub Repo Processing Skill',
    version: 4,
    spec: {
      updates: [
        {
          id: 'root',
          component: {
            type: 'Column',
            children: ['skill-meta-card', 'skill-editor'],
          },
        },
        {
          id: 'skill-meta-card',
          component: {
            type: 'Card',
            props: {
              title: 'GitHub Repo Processing',
              description:
                'Retrieves and stores GitHub repository content. Fetches README and repo metadata via web browse, creates a document in the KB.',
            },
            children: ['skill-meta-row'],
          },
        },
        {
          id: 'skill-meta-row',
          component: {
            type: 'Row',
            props: { gap: 2 },
            children: ['skill-status-badge', 'skill-created-text'],
          },
        },
        {
          id: 'skill-status-badge',
          component: {
            type: 'Badge',
            props: { text: 'active', variant: 'success' },
          },
        },
        {
          id: 'skill-created-text',
          component: {
            type: 'Text',
            props: {
              text: 'Created: 2026-03-04 | Updated: 2026-03-21',
              variant: 'muted',
            },
          },
        },
        {
          id: 'skill-editor',
          component: {
            type: 'MarkdownEditor',
            props: {
              content: skillEditorMarkdown,
              editable: true,
              title: 'Instructions',
            },
            actions: [{ name: 'save_skill', label: 'Save Changes' }],
          },
        },
      ],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  // Single-surface canvas with TWO MarkdownEditors (no maxHeight) — mirrors
  // the AICC-633 implementation-plan surface. Two fill anchors means no
  // unbroken root→anchor chain, so the surface must stay content-sized and
  // grow to include everything (regression: the Card was pinned to the panel
  // height while the root column overflowed past its bottom border).
  {
    canvasId: 'cvs-two-editors',
    surfaceId: 'srf-two-editors',
    runId: 'run-plan',
    title: 'AICC-633 — Implementation Plan',
    version: 1,
    spec: {
      updates: [
        {
          id: 'root',
          component: {
            type: 'Column',
            props: { gap: 4 },
            children: ['status-row', 'blocked-alert', 'flow-card', 'plan-editor'],
          },
        },
        {
          id: 'status-row',
          component: {
            type: 'Row',
            props: { gap: 2 },
            children: ['b1', 'b2', 'b3'],
          },
        },
        {
          id: 'b1',
          component: { type: 'Badge', props: { variant: 'info', text: 'Story · AICC-633' } },
        },
        {
          id: 'b2',
          component: { type: 'Badge', props: { variant: 'error', text: 'Development Blocked' } },
        },
        {
          id: 'b3',
          component: {
            type: 'Badge',
            props: { variant: 'success', text: 'Server side ready (OCC-3407/3492)' },
          },
        },
        {
          id: 'blocked-alert',
          component: {
            type: 'Alert',
            props: {
              variant: 'warning',
              title: 'Resolve 3 requirement decisions before coding',
              description:
                'The blocker is requirements sign-off, not code. Each decision is isolated to one function in the plan:\n\n- **AF.5** — `patientDoB` is required in the spec but unavailable when identity is unverified.\n- **AICC-841** — `isOnshore` when unknown: default `false` (this ticket) vs `null` (AICC-841).\n- **401/500** — error handling: wrap up the call vs default-intent fallback (AICC-597).',
            },
          },
        },
        {
          id: 'flow-card',
          component: {
            type: 'Card',
            props: {
              title: 'Routing flow — before vs after',
              description: 'Reroute decision moves from VoiceAI (local) to OCC (centralized)',
            },
            children: ['flow-editor'],
          },
        },
        {
          id: 'flow-editor',
          component: {
            type: 'MarkdownEditor',
            props: {
              title: 'Sequence',
              minHeight: '460px',
              content:
                '```mermaid\nflowchart TD\n    A[Escalation triggered\\nescalateCallToHuman] --> B{Build payload\\nbuildRouteToQueueByIntentRequest}\n    B --> C[Map fields to TES camelCase\\nintent, isEscalation, bu, locale, isOnshore]\n    C --> D{Verification state + direction}\n    D -->|Inbound, unverified| E[phone + HIPAA status only\\nisHipaaVerified=false]\n    D -->|Outbound, unverified| F[all patient info\\nisHipaaVerified=false]\n    D -->|Verified| G[all patient fields\\nisHipaaVerified=true]\n    E --> H[POST /tes/calls/callSid/route-to-queue-by-intent\\nscope natera:occ_api]\n    F --> H\n    G --> H\n    H -->|2xx queueName| I[Use OCC-returned queue\\nreplaces getQueueNameForEscalation]\n    H -->|4xx / 5xx| J[Log + VOICE_BACKEND_PROBLEM\\nwrap up OR default intent - OPEN]\n    subgraph RETIRED [Retired: AICC-609 local logic]\n      K[getQueueNameForEscalation\\nintent to queue mapping] -.feature-flag off.-> I\n    end\n```\n\n**Key change:** VoiceAI stops resolving `queueName` locally. It sends call metadata to OCC, and OCC\'s `intent_routing_mapping` table returns the queue.',
            },
          },
        },
        {
          id: 'plan-editor',
          component: {
            type: 'MarkdownEditor',
            props: {
              title: 'Full plan (plans/AICC-633-plan.md)',
              minHeight: '620px',
              content: overflowPlanMarkdown,
            },
          },
        },
      ],
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

export default function CanvasPanelPreviewPage() {
  const eventCtx = useMemo(() => noopEventContext, []);
  const initialCanvas = useMemo(
    () => ({ canvas: fakeCanvases[0], surfaces: fakeSurfaces }),
    [],
  );
  // Hint the provider about every canvas so tabs render.
  const canvasHints = useMemo(
    () =>
      fakeCanvases.map((w) => ({
        canvasId: w.canvasId,
        title: w.title,
        isDefault: w.isDefault,
        layout: w.layout,
        createdAt: w.createdAt,
      })),
    [],
  );

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="border-b border-border px-6 py-4">
        <h1 className="text-2xl font-bold">Canvas Panel Preview</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Hard-coded multi-canvas state. Used for visual validation of the
          surface header action menu, canvas tabs, and surface layout. Dev only.
        </p>
      </header>
      <div className="flex-1 overflow-hidden">
        <PlatformEventContext.Provider value={eventCtx}>
          <CanvasProvider
            conversationId={CONV_ID}
            canvasHints={canvasHints}
            initialCanvas={initialCanvas}
          >
            <DevHighlightChips />
            <CanvasPanel />
          </CanvasProvider>
        </PlatformEventContext.Provider>
      </div>
    </div>
  );
}
